import { ReactNode, createContext, useContext, useEffect, useState } from 'react'

import * as api from 'api/api'
import { AppSettings } from 'api/types'
import {
  AppLocale,
  RegionSettings,
  readStoredLocale,
  setRegionSettings,
  storeLocale,
} from 'lib/region'

const toRegion = (settings: AppSettings): RegionSettings => ({
  currency: settings.currency,
  dateFormat: settings.dateFormat,
  defaultLocale: settings.defaultLocale,
  exportLocale: settings.exportLocale,
  latinDigits: settings.latinDigits,
  timeFormat: settings.timeFormat,
  units: 'metric',
})

const RegionContext = createContext<{
  locale: AppLocale
  settings: AppSettings | null
  setLocale: (locale: AppLocale) => void
  adoptSettings: (settings: AppSettings) => void
}>({
  adoptSettings: () => undefined,
  locale: 'ar',
  setLocale: () => undefined,
  settings: null,
})

/** Organisation region (dates, time, digits, currency) plus the person's language.
 *  Language is per browser, like theme: an explicit choice wins over the organisation default. */
export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [locale, setLocaleState] = useState<AppLocale>(readStoredLocale() ?? 'ar')

  const adoptSettings = (next: AppSettings) => {
    setSettings(next)
    setRegionSettings(toRegion(next))
    if (!readStoredLocale()) setLocaleState(next.defaultLocale)
  }

  useEffect(() => {
    api
      .fetchAppSettings()
      .then((next) => {
        setRegionSettings(toRegion(next))
        setSettings(next)
        if (!readStoredLocale()) setLocaleState(next.defaultLocale)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
    document.title = locale === 'en' ? 'JoPath — Daily project reports' : 'JoPath - التقارير اليومية للمشاريع'
  }, [locale])

  const setLocale = (next: AppLocale) => {
    storeLocale(next)
    setLocaleState(next)
  }

  return <RegionContext.Provider value={{ adoptSettings, locale, setLocale, settings }}>{children}</RegionContext.Provider>
}

export const useRegion = () => useContext(RegionContext)
